import { z } from 'zod'
import { timestampSchema, uuidSchema } from './common.ts'

/**
 * What a file can be pinned to. The database stores this as nullable foreign keys guarded by
 * `CHECK (num_nonnulls(...) = 1)` rather than a polymorphic `entity_id` — see ADR 0003 D3. This
 * union is that constraint expressed in the contract, so a caller cannot name two parents.
 */
export const DOCUMENT_ATTACHMENT_ENTITIES = [
  'deal',
  'client',
  'order',
  'invoice',
  'paymentRequest',
  'proposalRevision',
] as const
export const documentAttachmentEntitySchema = z.enum(DOCUMENT_ATTACHMENT_ENTITIES)

export const ATTACHMENT_ENTITIES = [...DOCUMENT_ATTACHMENT_ENTITIES, 'material'] as const

export const attachmentEntitySchema = z.enum(ATTACHMENT_ENTITIES)
export type AttachmentEntity = z.infer<typeof attachmentEntitySchema>

export const attachmentTargetSchema = z.object({
  entity: attachmentEntitySchema,
  id: uuidSchema,
})

export type AttachmentTarget = z.infer<typeof attachmentTargetSchema>

/** What a file on an order is, so a contract is never lost among drawings. */
export const ATTACHMENT_FILE_KINDS = ['contract', 'drawing', 'client_letter', 'other'] as const
export const attachmentFileKindSchema = z.enum(ATTACHMENT_FILE_KINDS)
export type AttachmentFileKind = z.infer<typeof attachmentFileKindSchema>

export const ATTACHMENT_FILE_KIND_LABELS: Record<AttachmentFileKind, string> = {
  contract: 'Договір',
  drawing: 'Креслення',
  client_letter: 'Лист клієнта',
  other: 'Інше',
}

export const attachmentSchema = z.object({
  id: uuidSchema,
  fileKey: z.string().min(1),
  fileName: z.string().min(1),
  contentType: z.string().min(1),
  sizeBytes: z.number().int().nonnegative(),
  uploadedBy: z.string().min(1),
  createdAt: timestampSchema,
})

export type Attachment = z.infer<typeof attachmentSchema>

/** An attachment as it appears against one parent, which is how every read path wants it. */
export const linkedAttachmentSchema = attachmentSchema.extend({
  linkId: uuidSchema,
  target: attachmentTargetSchema,
  uploaderName: z.string().nullable(),
  versionSeriesId: uuidSchema,
  versionNumber: z.number().int().positive(),
  kind: attachmentFileKindSchema,
})

export type LinkedAttachment = z.infer<typeof linkedAttachmentSchema>

export const uploadAttachmentInputSchema = z.object({
  target: attachmentTargetSchema,
  versionOfLinkId: uuidSchema.optional(),
  /** Omitted: «Інше» for a new file, the previous version's type for a new version. */
  kind: attachmentFileKindSchema.optional(),
})

/** Presigned download link. Short-lived on purpose — the object itself stays private. */
export const attachmentDownloadSchema = z.object({
  url: z.string(),
  expiresInSeconds: z.number().int().positive(),
})

export type AttachmentDownload = z.infer<typeof attachmentDownloadSchema>
